import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

// Note: The 'projects' table types will be available after running migrations
// and regenerating Supabase types with: npx supabase gen types typescript

export interface Project {
  id: string;
  user_id: string;
  name: string;
  description: string | null;
  color: string;
  created_at: string;
  updated_at: string;
  task_count?: number;
}

interface UseProjectsReturn {
  projects: Project[];
  isLoading: boolean;
  error: string | null;
  createProject: (name: string, description?: string, color?: string) => Promise<Project | null>;
  updateProject: (id: string, data: Partial<Pick<Project, 'name' | 'description' | 'color'>>) => Promise<boolean>;
  deleteProject: (id: string) => Promise<boolean>;
  refetch: () => Promise<void>;
}

export function useProjects(): UseProjectsReturn {
  const { user } = useAuth();
  const [projects, setProjects] = useState<Project[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchProjects = useCallback(async () => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      // Get profile ID
      const { data: profile, error: profileError } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (profileError) throw new Error("Erro ao buscar perfil");

      // Get projects with task count
      const { data: projectsData, error: projectsError } = await supabase
        .from("projects")
        .select(`
          *,
          tasks:tasks(count)
        `)
        .eq("user_id", profile.id)
        .order("created_at", { ascending: false });

      if (projectsError) throw new Error("Erro ao buscar projetos");

      // Transform data to include task_count
      const projectsWithCount = (projectsData || []).map((p: any) => ({
        ...p,
        task_count: p.tasks?.[0]?.count || 0,
      }));

      setProjects(projectsWithCount);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Erro desconhecido");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  const createProject = async (name: string, description?: string, color?: string): Promise<Project | null> => {
    if (!user) return null;

    try {
      const { data: profile } = await supabase
        .from("profiles")
        .select("id")
        .eq("user_id", user.id)
        .single();

      if (!profile) throw new Error("Perfil não encontrado");

      const { data, error } = await supabase
        .from("projects")
        .insert({
          user_id: profile.id,
          name,
          description: description || null,
          color: color || "#14b8a6",
        })
        .select()
        .single();

      if (error) throw error;

      // Add to local state
      const newProject = { ...data, task_count: 0 } as Project;
      setProjects(prev => [newProject, ...prev]);
      return newProject;
    } catch (err) {
      console.error("Error creating project:", err);
      return null;
    }
  };

  const updateProject = async (id: string, updateData: Partial<Pick<Project, 'name' | 'description' | 'color'>>): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("projects")
        .update(updateData)
        .eq("id", id);

      if (error) throw error;

      // Update local state
      setProjects(prev => prev.map(p => p.id === id ? { ...p, ...updateData } : p));
      return true;
    } catch (err) {
      console.error("Error updating project:", err);
      return false;
    }
  };

  const deleteProject = async (id: string): Promise<boolean> => {
    try {
      const { error } = await supabase
        .from("projects")
        .delete()
        .eq("id", id);

      if (error) throw error;

      // Remove from local state
      setProjects(prev => prev.filter(p => p.id !== id));
      return true;
    } catch (err) {
      console.error("Error deleting project:", err);
      return false;
    }
  };

  useEffect(() => {
    fetchProjects();
  }, [fetchProjects]);

  return {
    projects,
    isLoading,
    error,
    createProject,
    updateProject,
    deleteProject,
    refetch: fetchProjects,
  };
}
